import { Box } from '@chakra-ui/react'
import { type NexiCreatePaymentResponse } from 'ecosystem/nexi'
import { useRefUpdated } from 'ui/hooks/useRefUpdated'
import Script from 'next/script'
import { useCallback, useEffect, useRef } from 'react'
import { getFullLocale } from 'shared-utils'
import { type Language } from 'ecosystem'
import { type DibsCheckout } from './types'

const NEXI_URL = process.env.NEXT_PUBLIC_NEXI_CHECKOUT_URL
const SCRIPT_URL = `${NEXI_URL}/v1/checkout.js?v=1`
const NEXI_CHECKOUT_WIDGET_KEY = process.env.NEXT_PUBLIC_NEXI_CHECKOUT_KEY ?? ''
const WIDGET_CONTAINER_ID = 'nexi-checkout-widget-container'

export interface NexiCheckoutProps {
  nexiPaymentId?: NexiCreatePaymentResponse['paymentId']
  onReady?: (checkoutInstance: DibsCheckout) => void
}

export const NexiCheckout = ({ nexiPaymentId, onReady }: NexiCheckoutProps) => {
  const isCheckoutCreated = useRef(false)
  const checkoutInstance = useRef<DibsCheckout>(undefined)
  const readyTimeoutId = useRef<ReturnType<typeof setTimeout>>(undefined)

  const handleIsReadyRef = useRefUpdated(onReady)
  const handleIsReady = useCallback(() => {
    if (!nexiPaymentId || isCheckoutCreated.current) {
      return
    }

    const language = document.documentElement.lang

    const checkoutOptions = {
      checkoutKey: NEXI_CHECKOUT_WIDGET_KEY,
      paymentId: nexiPaymentId,
      containerId: WIDGET_CONTAINER_ID,
      language: getFullLocale((language as Language) || 'sv')
    }

    const checkout = new window.Dibs.Checkout(checkoutOptions)

    checkoutInstance.current = checkout

    // The SDK calls its 'payment-completed' handler without checking that one was
    // registered, so a widget nobody subscribed to throws when the iframe reports
    // success (the receipt widget re-emits it for an already completed payment).
    // Subscribe eagerly — `on()` assigns, so a handler passed via onReady wins.
    checkout.on('payment-completed', () => undefined)

    readyTimeoutId.current = setTimeout(() => {
      // wait until all inner vital nexi checkout request are finished
      handleIsReadyRef.current?.(checkout)
    }, 1000)

    isCheckoutCreated.current = true
  }, [handleIsReadyRef, nexiPaymentId])

  useEffect(() => {
    if (window.Dibs?.Checkout) {
      handleIsReady()
    }
  }, [handleIsReady])

  // The SDK filters its global 'message' listener by origin only — it never checks
  // which iframe sent the event. An instance left behind after unmount keeps
  // reacting to the next widget mounted elsewhere (the /confirmation receipt),
  // re-firing 'payment-completed' and re-running the previous page's redirect.
  useEffect(
    () => () => {
      clearTimeout(readyTimeoutId.current)
      checkoutInstance.current?.cleanup()
      checkoutInstance.current = undefined
      isCheckoutCreated.current = false
    },
    []
  )

  return (
    <>
      <Script src={SCRIPT_URL} onLoad={handleIsReady} />

      <Box width="100%">
        <div id={WIDGET_CONTAINER_ID} />
      </Box>
    </>
  )
}
